import type {
  MavisOpportunityQualification,
  MeddpiccKey,
  NowToValueKey,
} from '../../types/mavis';

const MEDDPICC_KEYS: MeddpiccKey[] = [
  'metrics', 'economic_buyer', 'decision_criteria', 'decision_process',
  'partners', 'paper_process', 'implicated_pain', 'champion', 'competition',
];

const NTV_KEYS: NowToValueKey[] = [
  'current_state', 'negative_consequences', 'future_state',
  'positive_business_outcomes', 'required_capabilities',
  'timelines_and_milestones', 'personas_required',
];

const THREE_WHYS_KEYS = ['why_change', 'why_now', 'why_us'] as const;

function pct(filled: number, total: number): number {
  return total === 0 ? 0 : Math.round((filled / total) * 100);
}

function barColor(p: number): string {
  if (p === 0) return 'bg-app-line';
  if (p < 34) return 'bg-red-400';
  if (p < 67) return 'bg-amber-400';
  if (p < 100) return 'bg-indigo-400';
  return 'bg-forest-500';
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const p = pct(value, max);
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="flex-1 h-1.5 bg-app-canvas rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor(p)}`}
          style={{ width: `${p}%` }}
        />
      </div>
      <span className="text-[11px] font-semibold tabular-nums text-app-muted w-8 text-right">
        {p}%
      </span>
    </div>
  );
}

interface StatBlockProps {
  label: string;
  filled: number;
  total: number;
}

function StatBlock({ label, filled, total }: StatBlockProps) {
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold text-app-muted uppercase tracking-wide truncate">
          {label}
        </span>
        <span className="text-xs text-app-muted flex-shrink-0 tabular-nums">
          {filled}/{total}
        </span>
      </div>
      <ProgressBar value={filled} max={total} />
    </div>
  );
}

interface Props {
  qualification: MavisOpportunityQualification | null;
}

export default function MavisQualificationScorecard({ qualification }: Props) {
  if (!qualification) {
    return (
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm px-5 py-4">
        <p className="text-xs text-app-muted text-center">Qualification record loading…</p>
      </div>
    );
  }

  const { meddpicc, now_to_value, three_whys } = qualification;

  const mFilled = MEDDPICC_KEYS.filter(k => (meddpicc[k]?.score ?? 0) > 0).length;
  const nFilled = NTV_KEYS.filter(k => (now_to_value[k]?.value ?? '').trim() !== '').length;
  const wFilled = THREE_WHYS_KEYS.filter(k => (three_whys[k] ?? '').trim() !== '').length;

  const mTotal = MEDDPICC_KEYS.length;     // 8
  const nTotal = NTV_KEYS.length;           // 7
  const wTotal = THREE_WHYS_KEYS.length;    // 3

  const overallFilled = mFilled + nFilled + wFilled;
  const overallTotal  = mTotal + nTotal + wTotal;  // 18
  const overallPct    = pct(overallFilled, overallTotal);

  return (
    <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm px-5 py-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 items-start">
        {/* Overall (prominent) */}
        <div className="sm:col-span-1 flex flex-col items-center justify-center py-1">
          <span className={`text-3xl font-bold tabular-nums ${barColor(overallPct).replace('bg-', 'text-')}`}>
            {overallPct}%
          </span>
          <span className="text-[11px] text-app-muted font-medium mt-0.5 uppercase tracking-wide">
            Overall
          </span>
          <span className="text-[10px] text-app-muted tabular-nums">
            {overallFilled}/{overallTotal} fields
          </span>
        </div>

        {/* Per-framework bars */}
        <div className="sm:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatBlock label="MEDDPICC"    filled={mFilled} total={mTotal} />
          <StatBlock label="Now to Value" filled={nFilled} total={nTotal} />
          <StatBlock label="3 Whys"       filled={wFilled} total={wTotal} />
        </div>
      </div>
    </div>
  );
}
